import {
  LABOR_KEYS,
  ORDER_BUNDLE_MSG,
  orderedMarkText,
  type OrderRef,
  LABOR_LABEL,
  laborSum,
  AXES_MSG,
  COST_KIND_LABEL,
  COST_KIND_SHORT,
  costKindRateBase,
  ESTIMATE_COLUMN_LIMITS,
  CostKind,
  ESTIMATE_PC_MSG,
  EXCLUDED_WORDS,
  MARK_LABEL,
  RATE_BASE_COVER_LABEL,
  RATE_BASE_LABEL,
  RateBase,
  ROW_KIND_LABOR,
  ROW_KIND_LABOR_LABEL,
  ROW_ROLE_HELP,
  ROW_ROLE_HELP_AMT,
  ROW_ROLE_HELP_LABOR,
  ROW_ROLE_HELP_RATE_NO_LABOR,
  ROW_ROLE_LABEL,
  type RatePreset,
  RowRole,
  TAKEOFF_MSG,
  type EstimateLine,
  type LineInput,
  type LumpsumBasis,
  type MarkCell,
  type RevisionMarkLine,
  type RevisionMarkRemoved,
  type RevisionMarkRemovedSheet,
  type SheetInput,
  type RfqLineMark,
  TAX_CATEGORIES,
  TAX_CATEGORY_COL,
  TAX_CATEGORY_SHORT,
  TAX_MSG,
  taxCategoryNorm,
  type TaxCategory,
  type ReferralLineFee,
} from "@genbaryoku/contract";
import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { DateInput } from "../../components/DateInput.jsx";
import { NumInput } from "../../components/NumInput.jsx";
import { RowMenu, type RowMenuItem } from "../../components/RowMenu.jsx";
import { UnitInput } from "../../components/UnitInput.jsx";
import { Icon } from "../../components/Shell.jsx";
import { Help } from "../../components/ui.jsx";
import { lineSpecLabel, t } from "../../labels.js";
import { CostSource } from "../../components/VendorImport.jsx";
import { LineAttMark } from "../../components/LineAttachments.jsx";
import { fq, yen } from "../../lib/num.js";
import { MONEY_KEYS, NAME_LEFT, SIZABLE, minWidthOf, type ColDef, type MoneyKey, type MoneyTexts, type SizableKey } from "./columns.js";
import { presetWidth, type ColPreset, type ColSizer } from "./colWidths.js";
import { rfqMarkView, rfqMarksView } from "../rfq/parts.jsx";
import { FeeMark, LinePricing, type LinePricingCtx } from "../../components/ReferralParts.jsx";

/** {order}の行の内訳の文（#233） */
const ORDER_LINE_MSG = { breakdown: "内訳", amount: "金額", mismatch: "内訳の合計 {sum} が金額と違います" } as const;

/* ============================================================================
   見積書の表（#131、docs/47 §12、docs/10 §9.1。Figma PC1・D2・D3 → 2回目は PC2 簡潔版 53:201）。行は 44px で1段。
   ・紙の列と社内用の列は columns.ts の定義で分ける（見出し・幅・colSpan）。社内用は地色を変え、左に縦の区切り線
   ・行はその行の中で書く（押すと入力欄。下に「行の詳細」が2段で開く＝上段 区分・場所・工種・材工・外注・拾い出し／下段 仕入元・下に追加・削除・×（閉じる）。#139）
   ・並べ替えは No の左のつまみ（ドラッグ。押すと「上へ」「下へ」＝ドラッグだけにしない）
   ・社内用の列：仕入れ（上段＝押すとその場で直せる／自社／未入力／—、下段＝利益率・原価割れは朱の文字。#139 で利益率の列をまとめた）・出どころ（外注先のしるし・自社・手入力・依頼中・写真・率）
   ・行に乗せたときだけ「取込」と「…」（D2）。キーボードで入ったときも出す
   金額は DB が計算したものを見せるだけ（画面では計算しない）
   ============================================================================ */

export type Row = LineInput;
export type Sheet = SheetInput;
export type CostSum = { sum: number; missing: number; any: boolean };
/** 行の拾い出しまわり（#130）。ふつうの行だけ。qty＝紙に出す数量、basis＝一式の印、childOf＝内訳の行なら親の名前（親が消えていれば null） */
export type RowTakeoff = { qty: number; count: number; basis: LumpsumBasis | null; childOf: string | null | undefined; onOpen: () => void };
export type CostRef = NonNullable<EstimateLine["costRef"]>;
export type AxisName = { short: string; full: string } | null;
/** 表ぜんたいの決まり（行が同じものを見る） */
export type TblCtx = {
  cols: readonly ColDef[];
  showInternal: boolean;
  /** 出したあとの紙（書けない。仕入れだけは直せる） */
  readOnly: boolean;
  /** 雛形・請求書（取込・写真・依頼は無い） */
  alt: boolean;
  onCover: boolean;
  /**
   * モジュール field（現場力だけ。#142 段2、docs/52 §2）：労務費（率の基礎・行と見出しの印）・何の費用か（5項目）を出す。
   * 無い会社は隠すだけ（入っている値はそのまま保存し、金額も変わらない）
   */
  field: boolean;
  /**
   * 使う機能「{takeoff}」（#142 段3、docs/52 §3）：数量の下の点線（#190）・行の詳細の「{takeoff} n行」・数量を押すと引き出し。
   * OFF の会社は隠すだけ（拾い出しの行は数量をそのまま見せ、打てば手で打つに戻る）
   */
  takeoff: boolean;
  /**
   * 税区分の列（T2、../eigyouryoku/docs/14）：使う機能「軽減税率・税区分」（reducedTax）が ON の会社だけ、金額の右に出す。
   * OFF の会社は行の詳細（2段目）の「税区分」から選ぶ（立替金・非課税だけ使う会社のため）。データと紙はスイッチに関係なく同じ
   */
  taxCol: boolean;
  /** 取引日の欄を出すか（T2。API の taxDateHint）。true のときだけ行の詳細の2段目に行の「取引日」 */
  taxDateHint: boolean;
  /**
   * 単価・金額・仕入れの列でいちばん長い額（#190：金額は省略しない）。表はこの字が入る幅まで列を広げる（既定の幅が最小）。
   * 小計・合計・「○○ 計」の行は単価〜金額の2列を結合するので数えない（moneyTextsOf）
   */
  money?: MoneyTexts | undefined;
  /**
   * #199（docs/80）：基礎（選んだ小計・選んだ行）が不使用の内訳にある率の行の id。DB はその基礎を 0 にする（例外にしない）＝画面は ⚠ で知らせる
   */
  rateBaseUnused?: ReadonlySet<string> | undefined;
  /**
   * パートナー（#213、docs/40 §6.1）：見積に付いているときだけ。行ごとの率と手数料（使う内訳の行）。
   * 社内用の「仕入れ」の欄の下段の利益率は手数料後、「仕入元」の欄の下段に手数料「P 14,286」
   */
  referral?: { pct: number; byLine: Readonly<Record<string, ReferralLineFee>> } | null | undefined;
  /** 行の詳細の下段の 率で／円で・粗利率・パートナー・上乗せ（#213、docs/40 §5）。書ける見積で社内用を表示しているときだけ */
  pricing?: LinePricingCtx | undefined;
  /** {order}の行の内訳（#233、docs/93 §4.3）：field の会社の{order}だけ、ふつうの行の詳細に 材料費・労務費・法定福利費・安全衛生経費 */
  laborBreakdown?: boolean | undefined;
  /** 労務費の内訳（内訳タブの ⋯「労務費」）の id（#305）：その内訳の明細の行にも名称の右の札「労務費」 */
  laborSheets?: ReadonlySet<string> | undefined;
  /** 会社の率の名前（#306）：率の行で費目を選んだとき、率が空なら同じ費目の最初の率を入れる */
  ratePresets?: readonly RatePreset[] | undefined;
};
/** 率の行の対象の名前（#309。表紙の枠は「内訳の合計＋表紙の枠」） */
export const rateTargetLabel = (base: RateBase | null, onCover: boolean): string => {
  const b = base ?? "block";
  return b === "block" && onCover ? RATE_BASE_COVER_LABEL : RATE_BASE_LABEL[b];
};
/** 明細の行が労務費か（#305）：行の印か、労務費の内訳の行 */
export const isLaborRow = (r: Pick<Row, "role" | "isLabor" | "sheetId">, ctx: Pick<TblCtx, "laborSheets">): boolean =>
  r.role === "detail" && (r.isLabor || (!!r.sheetId && !!ctx.laborSheets?.has(r.sheetId)));
/**
 * 区分「労務費」の行（#310、11/5 射場さん ★1）：区分の選ぶ欄が「労務費」になる行＝field の会社の明細で行の労務費の印。
 * 行の詳細に 材工 と 拾い出し（「拾い出し n行 ›」・数量の点線）を出さない（値は消さない。拾い出しのある行の数量はそのまま）
 */
export const laborKindRow = (r: Pick<Row, "role" | "isLabor">, ctx: Pick<TblCtx, "field">): boolean => !!ctx.field && r.role === "detail" && r.isLabor;
/**
 * 並べ替え（手動のときだけドラッグ。並べ方のときは止めて知らせる）。
 * つまみを押す（ドラッグしない）と「上へ」「下へ」のメニュー（moves）＝ドラッグだけの操作にしない（WCAG 2.2 SC 2.5.7）
 */
export type DragProps = { enabled: boolean; locked: boolean; armed: boolean; drop: "before" | "after" | null; moves: RowMenuItem[]; onArm: () => void; onLocked: () => void; onStart: (e: React.DragEvent) => void; onOver: (e: React.DragEvent) => void; onDrop: (e: React.DragEvent) => void; onEnd: () => void };

export const NUMBERED: ReadonlySet<RowRole> = new Set<RowRole>(["detail", "overhead_rate", "excluded", "discount"]);
/**
 * 単位（#164）：会社の単位の一覧（設定 → 単位）を候補に出す欄（components/UnitInput.tsx）。打った頭の文字で絞り、一覧に無い単位もそのまま打てる。
 * 品目に既定の単位があれば先頭に。Enter で次の欄へ（候補を ↓↑ で選んでいればそれを入れて）
 */
export function UnitSelect({ value, preferred, onChange }: { value: string; preferred?: string | null | undefined; onChange: (v: string) => void }) {
  return <UnitInput id="f_unit" className="ce unitin" value={value} preferred={preferred} onChange={onChange} testId="unit-input" />;
}
export const pct = (rate: number | null) => (rate == null ? "" : fq(Math.round(rate * 10000) / 100));

/* ---------------------------------------------------------------- 税区分（T2） */
/** 税区分を持つ行（金額の付く行）。別途・小計・見出し・文章は持たない */
const TAX_ROLES: ReadonlySet<RowRole> = new Set<RowRole>(["detail", "overhead_rate", "discount"]);
export const taxCatOf = (r: Pick<Row, "taxCategory">): TaxCategory => taxCategoryNorm(r.taxCategory);
/**
 * 税区分を選ぶ欄（T2）：列（72px）は前の短い名前（#304：標準・軽減・新聞・輸出・非課税・不課税・立替）、
 * 行の詳細（full）は「軽減(食料)」の形（#303 A3：標準・軽減(食料)・軽減(新聞)・免税(輸出)・非課税・不課税・立替金）。
 * appearance:none＋自前の矢印（.taxsel）。乗せると「軽減(食料)」の形
 */
export function TaxSelect({ value, onChange, id = "f_tax", full = false }: { value: TaxCategory; onChange: (c: TaxCategory) => void; id?: string; full?: boolean }) {
  return (
    <span className={"taxsel" + (full ? " full" : "")}>
      <select className="ce" id={id} value={value} aria-label={TAX_MSG.category} title={TAX_CATEGORY_SHORT[value]} data-testid={id === "f_tax" ? "tax-select" : "tax-select-detail"} onChange={(x) => onChange(x.target.value as TaxCategory)}>
        {TAX_CATEGORIES.map((c) => (
          <option key={c} value={c}>{full ? TAX_CATEGORY_SHORT[c] : TAX_CATEGORY_COL[c]}</option>
        ))}
      </select>
    </span>
  );
}
/** 列の税区分（見る）。金額の付く行だけ列の短い名前（標準は薄く）・乗せると「軽減(食料)」の形（#304） */
const taxViewTd = (ctx: TblCtx, r: Row) => {
  if (!ctx.taxCol) return null;
  if (!TAX_ROLES.has(r.role)) return <td className="tax" />;
  const c = taxCatOf(r);
  return (
    <td className="tax" data-testid="tax-cell">
      <span className={"taxv" + (c === "standard" ? " muted" : "")} title={TAX_CATEGORY_SHORT[c]}>{TAX_CATEGORY_COL[c]}</span>
    </td>
  );
};
/** 列の空のセル（金額の付かない行・合計の行） */
const taxEmptyTd = (ctx: TblCtx) => (ctx.taxCol ? <td className="tax" /> : null);
const pctText = (m: number) => `${m.toFixed(1)}%`;
const internalTds = (ctx: TblCtx, cls = "") => (ctx.showInternal ? ctx.cols.filter((c) => c.group === "internal").map((c) => <td key={c.key} className={`int ${c.key} ${cls}`.trim()} />) : null);
const paperCount = (ctx: TblCtx) => ctx.cols.filter((c) => c.group === "paper").length;

/* ---------------------------------------------------------------- 表の枠 */
const thDots = (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <circle cx="5" cy="12" r="2" />
    <circle cx="12" cy="12" r="2" />
    <circle cx="19" cy="12" r="2" />
  </svg>
);
const PRESET_LABEL: Record<ColPreset, string> = { narrow: ESTIMATE_PC_MSG.colNarrow, normal: ESTIMATE_PC_MSG.colNormal, wide: ESTIMATE_PC_MSG.colWide };
/**
 * 幅を変えられる列の見出し（#140）：名前・「…」（狭い／標準／広い／既定に戻す）・右端の境目（ドラッグ。←→ のキーでも 8px ずつ）。
 * ドラッグの間は表の枠だけを描き直し（onLive）、離したときに1回だけ幅を決める（sizer.set → 500ms 後に保存）
 */
function SizableHead({ c, sizer, width, onLive }: { c: ColDef; sizer: ColSizer; width: number; onLive: (px: number | null) => void }) {
  const key = c.key as SizableKey;
  const L = ESTIMATE_COLUMN_LIMITS[key];
  const other = key === "spec" ? sizer.widths.note : sizer.widths.spec;
  const max = ESTIMATE_COLUMN_LIMITS.sum - other;
  const drag = useRef<{ x: number; w: number; id: number; px: number } | null>(null);
  const clamp = (px: number) => Math.min(Math.max(Math.round(px), L.min), max);
  const items: RowMenuItem[] = [
    ...(["narrow", "normal", "wide"] as const).map((p) => ({ label: PRESET_LABEL[p], checked: sizer.widths[key] === presetWidth(key, p), onSelect: () => sizer.preset(key, p), testId: `col-${key}-${p}` })),
    { label: ESTIMATE_PC_MSG.colReset, divider: true, onSelect: sizer.reset, testId: `col-${key}-reset` },
  ];
  return (
    <>
      <span className="thl">{c.label}</span>
      <RowMenu label={ESTIMATE_PC_MSG.colWidthMenu.replace("{name}", c.label)} items={items} testId={`col-menu-${key}`} trigger={thDots} triggerClass="thmore" />
      <span
        className="colgrip"
        role="separator"
        aria-orientation="vertical"
        aria-label={ESTIMATE_PC_MSG.colWidth.replace("{name}", c.label)}
        aria-valuenow={width}
        aria-valuemin={L.min}
        aria-valuemax={max}
        tabIndex={0}
        title={ESTIMATE_PC_MSG.colWidth.replace("{name}", c.label)}
        data-testid={`col-resize-${key}`}
        onPointerDown={(e) => {
          if (e.button !== 0) return;
          e.preventDefault();
          (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
          drag.current = { x: e.clientX, w: width, id: e.pointerId, px: width };
        }}
        onPointerMove={(e) => {
          const d = drag.current;
          if (!d || d.id !== e.pointerId) return;
          d.px = clamp(d.w + e.clientX - d.x);
          onLive(d.px);
        }}
        onPointerUp={(e) => {
          const d = drag.current;
          if (!d || d.id !== e.pointerId) return;
          drag.current = null;
          onLive(null);
          if (d.px !== d.w) sizer.set(key, d.px);
        }}
        onPointerCancel={() => {
          drag.current = null;
          onLive(null);
        }}
        onKeyDown={(e) => {
          const step = e.key === "ArrowRight" ? 8 : e.key === "ArrowLeft" ? -8 : 0;
          if (!step) return;
          e.preventDefault();
          sizer.set(key, clamp(width + step));
        }}
      />
    </>
  );
}

/**
 * 金額の列（単価・金額・仕入れ）は省略しない（#190）：いちばん長い額が入る幅（左右の余白 8px ずつ＋2px）まで列を広げる。既定の幅が最小。
 * 測るのは見えない見本の字（セルと同じ字・大きさ・太さ）＝字の読み込みや文字サイズの切替で幅が変わったら ResizeObserver が測り直す
 */
const MONEY_PAD = 18;
function useMoneyFit(money: MoneyTexts | undefined) {
  const box = useRef<HTMLSpanElement>(null);
  const [fit, setFit] = useState<Partial<Record<MoneyKey, number>>>({});
  const key = MONEY_KEYS.map((k) => money?.[k] ?? "").join("|");
  useLayoutEffect(() => {
    const el = box.current;
    if (!el) return;
    const measure = () => {
      const next: Partial<Record<MoneyKey, number>> = {};
      for (const k of MONEY_KEYS) {
        const sp = el.querySelector<HTMLElement>(`[data-k="${k}"]`);
        if (sp?.textContent) next[k] = Math.ceil(sp.getBoundingClientRect().width) + MONEY_PAD;
      }
      setFit((prev) => (MONEY_KEYS.every((k) => prev[k] === next[k]) ? prev : next));
    };
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(measure);
    el.querySelectorAll("[data-k]").forEach((x) => ro.observe(x));
    return () => ro.disconnect();
  }, [key]);
  const probe = (
    <span ref={box} className="etprobe" aria-hidden="true">
      {MONEY_KEYS.map((k) => (
        <span key={k} data-k={k} className={"etprobe-" + k}>{money?.[k] ?? ""}</span>
      ))}
    </span>
  );
  return { fit, probe };
}

export function EstimateTable({ ctx, customer, children, foot, testId, sizer }: { ctx: TblCtx; customer: string; children: ReactNode; foot: ReactNode; testId?: string; sizer?: ColSizer | undefined }) {
  // ドラッグの途中の幅（#140）：この枠だけ描き直す（行は描き直さない）
  const [live, setLive] = useState<{ key: SizableKey; px: number } | null>(null);
  const { fit, probe } = useMoneyFit(ctx.money);
  const cols = (live ? ctx.cols.map((c) => (c.key === live.key ? { ...c, width: live.px } : c)) : ctx.cols).map((c) => {
    const w = (MONEY_KEYS as readonly string[]).includes(c.key) ? fit[c.key as MoneyKey] : undefined;
    return w != null && c.width != null && w > c.width ? { ...c, width: w } : c;
  });
  const minW = minWidthOf(cols);
  // 見えている表の幅（--etw）：行の詳細の1行を、表を横に流しているときも見えている幅の中に置く（右の「下に追加」「消す」「閉じる」が隠れない）
  const wrap = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = wrap.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const set = () => el.style.setProperty("--etw", `${el.clientWidth}px`);
    set();
    const ro = new ResizeObserver(set);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return (
    <div ref={wrap} className={"etwrap" + (ctx.showInternal ? " withint" : "") + (live ? " is-sizing" : "")} data-testid={testId}>
      {probe}
      <table className="etbl" style={{ minWidth: minW }}>
        <colgroup>
          {cols.map((c) => (
            <col key={c.key} className={`c-${c.key}`} style={c.width != null ? { width: c.width } : undefined} />
          ))}
        </colgroup>
        <thead>
          {/* #140：社内用の見出しの段（「社内用（{顧客}には出しません）」）はやめて、「仕入元」の右の「？」に */}
          <tr className="cols">
            {cols.map((c) => (
              <th key={c.key} className={[c.r ? "r" : "", c.group === "internal" ? "int" : "", c.key, c.sticky ? "stk" : "", sizer && SIZABLE.has(c.key) ? "sizable" : ""].filter(Boolean).join(" ")} style={c.key === "name" ? { left: NAME_LEFT } : undefined}>
                {sizer && SIZABLE.has(c.key) ? (
                  <SizableHead c={c} sizer={sizer} width={c.width ?? 0} onLive={(px) => setLive(px == null ? null : { key: c.key as SizableKey, px })} />
                ) : c.key === "source" ? (
                  <>
                    {c.label}
                    <Help label={ESTIMATE_PC_MSG.internalHelpLabel} text={ESTIMATE_PC_MSG.internalHead.replace("{customer}", customer)} testId="help-internal" />
                  </>
                ) : c.sub ? (
                  <span className="colsub">{c.label}<small>{c.sub}</small></span>
                ) : (
                  c.label
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody id="lineBody">{children}</tbody>
        <tfoot id="totals">{foot}</tfoot>
      </table>
    </div>
  );
}

/* ---------------------------------------------------------------- 合計の行（tfoot） */
/**
 * 合計の行。amount が null の行は金額の欄を空に（T2：免税・非課税・不課税は対象額だけで税額の欄は空）。
 * 金額は単価〜金額の2列を結合して右寄せ（#190：金額は省略しない。小計の行も同じ）
 */
export function TotalRow({ ctx, label, amount, id, cls, cost, sub }: { ctx: TblCtx; label: ReactNode; amount: number | null; id?: string; cls: string; cost?: { total: number; missing: number; amount: number; fee?: number | undefined } | undefined; sub?: boolean }) {
  return (
    <tr className={cls}>
      <td colSpan={5} className="r lbl">{label}</td>
      <td colSpan={2} className="r num amt" id={id}>{amount == null ? "" : yen(amount)}</td>
      {taxEmptyTd(ctx)}
      <td className="note" />
      {ctx.showInternal ? (
        cost ? (
          <>
            {/* 合計の行は全部入るまで率を出さない（ルール7）。未入力があれば 仕入元の欄に「仕入れ未入力 n行」（#139：利益率の列は無く、仕入れの欄の下段） */}
            <td className="int cost r num">
              {/* #213：パートナー ON の見積は 利益率＝手数料後（fee） */}
              <CostTwo top={<span data-testid={sub ? "sum-cost" : undefined}>{cost.total || !cost.missing ? yen(cost.total) : "—"}</span>} margin={cost.missing ? null : marginOf(cost.amount, cost.total + (cost.fee ?? 0))} marginTestId={sub ? "sum-margin" : undefined} />
            </td>
            {cost.missing ? <MissingCell n={cost.missing} testId={sub ? "cost-missing-count" : undefined} /> : <td className="int source" />}
          </>
        ) : (
          internalTds(ctx)
        )
      ) : null}
    </tr>
  );
}

/**
 * パートナー手数料の帯（#213、docs/40 §6.2）：合計の行の下。社内用を表示しているパートナー ON の見積だけ。
 * 「{referrer} 見本不動産 10%：手数料 14,286　手数料後の利益 28,572（20.0%）」。手数料後の利益がマイナスは朱（原価割れと同じ扱い）
 */
export function ReferralBandRow({ ctx, label, fee, profit, margin, missing }: { ctx: TblCtx; label: string; fee: number; profit: number | null; margin: number | null; missing: number }) {
  if (!ctx.showInternal) return null;
  const neg = profit != null && profit < 0;
  return (
    <tr className="tref" data-testid="referral-band">
      <td colSpan={ctx.cols.length}>
        <span className="trefin">
          <span className="trefp">{label}</span>
          <span>{t("手数料")} <b className="num" data-testid="referral-fee-total">{yen(fee)}</b></span>
          <span className={neg ? "shu" : ""}>
            {t("手数料後の利益")}{" "}
            {profit == null ? (
              <span className="muted" title={ESTIMATE_PC_MSG.costMissingCount.replace("{n}", String(missing))} data-testid="referral-profit">—</span>
            ) : (
              <b className="num" data-testid="referral-profit">{yen(profit)}{margin != null ? `（${pctText(margin)}）` : ""}</b>
            )}
            {neg ? <span className="under"> {ESTIMATE_PC_MSG.under}</span> : null}
          </span>
        </span>
      </td>
    </tr>
  );
}

function MarginText({ amount, cost }: { amount: number; cost: number }) {
  if (cost > amount) return <span className="under" data-testid="under">{ESTIMATE_PC_MSG.under}</span>;
  const m = amount > 0 ? Math.round(((amount - cost) / amount) * 1000) / 10 : null;
  return m == null ? <span className="muted">—</span> : <>{pctText(m)}</>;
}
/** 利益率（下段）。金額が 0 以下・仕入れが分からないときは出さない（「—」は上段だけ。#139） */
const marginOf = (amount: number, cost: number | null | undefined): ReactNode => (cost == null || amount <= 0 ? null : <MarginText amount={amount} cost={cost} />);
/**
 * 仕入れの欄の2段（#139、10/5 射場さん）：上段＝仕入れの額（自社・未入力・— も上段）、下段＝利益率（小さい文字・灰。原価割れは朱の「原価割れ」）。
 * 行の高さ 44px のまま。利益率が無いときは下段を置かない
 */
function CostTwo({ top, margin, marginTestId, marginId }: { top: ReactNode; margin?: ReactNode; marginTestId?: string | undefined; marginId?: string | undefined }) {
  return (
    <span className="c2">
      <span className="c2v">{top}</span>
      {margin != null ? <span className="c2m" id={marginId} data-testid={marginTestId}>{margin}</span> : null}
    </span>
  );
}
/** 小計・合計・内訳の合計の行で、仕入れが入っていない行があるとき：仕入元の欄に「仕入れ未入力 n行」 */
function MissingCell({ n, testId }: { n: number; testId?: string | undefined }) {
  return (
    <td className="int source missingcell">
      <span className="chip shu missing" data-testid={testId}>{ESTIMATE_PC_MSG.costMissingCount.replace("{n}", String(n))}</span>
    </td>
  );
}

/* ---------------------------------------------------------------- 社内用の3つのセル（ふつうの行・別途の行） */
function ImportButton({ done, onClick }: { done: boolean; onClick: () => void }) {
  return (
    <button type="button" className={"impbtn" + (done ? " done" : "")} data-testid="vimp" aria-label={ESTIMATE_PC_MSG.vendorImport} onClick={(e) => { e.stopPropagation(); onClick(); }}>
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M14 3h5a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-5M10 17l5-5-5-5M15 12H3" />
      </svg>
      {ESTIMATE_PC_MSG.vendorImport}
    </button>
  );
}
const dotsH = (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <circle cx="5" cy="12" r="2" />
    <circle cx="12" cy="12" r="2" />
    <circle cx="19" cy="12" r="2" />
  </svg>
);
/** 外注先のしるし（略称。乗せると全部の名前・出どころ） */
export const hardhat = (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M3 18h18M5 18v-3a7 7 0 0 1 14 0v3M10 8V5h4v3" />
  </svg>
);
export type SourceInfo = {
  costRef?: CostRef | undefined;
  /** 見積依頼に入れた（外注先を決めて、仕入れはまだ）。見積依頼書（#119）を送ったかで「未送付 ○○」「依頼済 ○○ 回答 10/12」「回答あり ○○」（乗せると正式名・番号・期限） */
  /** 外注先を付けて仕入れがまだの行。plain＝見積依頼（使う機能 rfq）が OFF の会社：しるしを出さず外注先の名前だけ（#149） */
  /**
   * #218（docs/88 §4.5）：marks＝その行の系譜の送付済・回答ありの依頼（何社でも）。あれば「依頼済 A社・B社」（回答のある社を先に「回答あり A社」）、
   * 無ければ外注先だけの「未送付 A社」。names＝{vendor}の id → 略称
   */
  requested?: { short: string; full: string; marks?: RfqLineMark[] | undefined; names?: ((vendorId: string) => string) | undefined; plain?: boolean | undefined } | undefined;
  /** 仕入れ「自社」（#131 の2回目。仕入れ 0・未入力に数えない） */
  own?: boolean | undefined;
  /** その行から作った{order}（#233 2a）。下段に「発注済 PO-0001」 */
  ordered?: OrderRef[] | undefined;
  att?: { photos: number; memos: number; onOpen: () => void } | undefined;
  onImport?: (() => void) | undefined;
  /** 外注先のしるしを押した：取込の1枚と原本 */
  onOpenSource?: (() => void) | undefined;
  menu: RowMenuItem[];
};
function SourceCell({ r, src, manual, fee }: { r: Row; src: SourceInfo; manual: boolean; fee?: ReferralLineFee | undefined }) {
  const ref = src.costRef;
  const detail = ref ? (ref.mode === "merge" && ref.sources.length ? `${ref.sources.length}行から（${ref.sources.map((s) => s.name).join("・")}）` : ref.mode === "split" && ref.sources[0] ? `${ref.sources[0].name} ${yen(ref.sources[0].base)} のうち` : "") : "";
  return (
    <td className={"int source" + (ref ? " has-ref" : "") + (fee || src.ordered?.length ? " has-fee" : "")} onClick={(e) => e.stopPropagation()}>
      <div className="srccell">
        <span className="srcmain">
          {r.role === "overhead_rate" ? (
            <span className="muted rate">{ESTIMATE_PC_MSG.rateRow}</span>
          ) : ref ? (
            <button type="button" className="vchip" data-testid="cost-ref" title={[`${ref.vendorName || ref.vendorShort || ""} ${ref.quoteNo}`.trim(), detail].filter(Boolean).join(" ・ ")} onClick={src.onOpenSource}>
              {hardhat}
              <span>{r.role === "excluded" ? "別途" : ref.vendorShort || ref.vendorName}</span>
            </button>
          ) : src.requested?.plain ? (
            <span className="mchip" data-testid="requested-vendor" title={src.requested.full}>{src.requested.short}</span>
          ) : src.requested?.marks?.length ? (
            (() => {
              const v = rfqMarksView(src.requested.marks, src.requested.names ?? (() => src.requested!.short));
              return (
                <span className={"rqchip" + (v.overdue ? " shu" : "")} data-testid="requested" data-state={v.state} title={v.title} aria-label={v.text}>
                  <Icon k="clock" />
                  <b>{v.head}</b>
                  <span className="rqnm">{v.tail}</span>
                </span>
              );
            })()
          ) : src.requested ? (
            (() => {
              const v = rfqMarkView(undefined, src.requested.short, src.requested.full);
              return (
                <span className={"rqchip is-unsent"} data-testid="requested" data-state={v.state} title={v.title} aria-label={v.text}>
                  <Icon k="clock" />
                  <b>{v.head}</b>
                  <span className="rqnm">{src.requested.short}</span>
                </span>
              );
            })()
          ) : src.own ? (
            <span className="mchip own" data-testid="cost-own-chip">{ESTIMATE_PC_MSG.own}</span>
          ) : manual ? (
            <span className="mchip" data-testid="cost-manual">
              <Icon k="pen" />
              {ESTIMATE_PC_MSG.manual}
            </span>
          ) : null}
          {src.att ? <LineAttMark photos={src.att.photos} memos={src.att.memos} onOpen={src.att.onOpen} /> : null}
        </span>
        <span className="hov">
          {/* 外注見積から入れた行は、外注先のしるしが取込の入口（押すと取込の1枚と原本）。乗せたときの「取込」は、まだ入れていない行だけ */}
          {src.onImport && !ref ? <ImportButton done={false} onClick={src.onImport} /> : null}
          {src.menu.length ? <RowMenu label={ESTIMATE_PC_MSG.rowMenu.replace("{name}", r.name || "この行")} items={src.menu} testId="row-more" trigger={dotsH} triggerClass="rowmore dots" /> : null}
        </span>
      </div>
      {/* #213（docs/40 §6.1）：パートナー ON の見積だけ、下段に手数料（藍の小さい字） */}
      {fee ? <FeeMark f={fee} /> : null}
      {/* #233 2a：その行から作った{order}「発注済 PO-0001」（下段・藍の小さい字。乗せると全部の番号） */}
      {src.ordered?.length ? (
        <span className="srcfee srcord" title={src.ordered.map((o) => o.no ?? t(ORDER_BUNDLE_MSG.alreadyDraft)).join("\n")} data-testid="line-ordered">
          {t(orderedMarkText(src.ordered))}
        </span>
      ) : null}
    </td>
  );
}
/** 値引きの行の仕入元の欄（#213：パートナー ON の見積は下段に手数料。値引きは負） */
function FeeOnlyCell({ fee }: { fee?: ReferralLineFee | undefined }) {
  return <td className={"int source" + (fee ? " has-fee" : "")}>{fee ? <FeeMark f={fee} /> : null}</td>;
}

/* ---------------------------------------------------------------- No の欄：つまみ（並べ替え。左端）・選ぶ □（まとめて付ける）・番号 */
const gripSvg = (
  <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="9" cy="6" r="1.6" /><circle cx="15" cy="6" r="1.6" /><circle cx="9" cy="12" r="1.6" /><circle cx="15" cy="12" r="1.6" /><circle cx="9" cy="18" r="1.6" /><circle cx="15" cy="18" r="1.6" /></svg>
);
function NoCell({ r, no, drag, select }: { r: Row; no: number | ""; drag?: DragProps | undefined; select?: { on: boolean; set: (on: boolean) => void } | undefined }) {
  return (
    <td className={"no stk" + (select?.on ? " sel" : "") + (r.role === "group" || r.role === "note" ? " merged" : "")} onClick={(e) => select && (e.target as HTMLElement).closest("input,.grip") && e.stopPropagation()}>
      <div className="nocell">
        {drag && !drag.locked && drag.moves.length ? (
          // 手動：つまみをドラッグで並べ替え。押す（ドラッグしない）と「上へ」「下へ」（ドラッグだけの操作にしない）
          <span className="gripwrap" onMouseDown={(e) => { e.stopPropagation(); drag.onArm(); }}>
            <RowMenu label={ESTIMATE_PC_MSG.gripMenu.replace("{name}", r.name || "この行")} items={drag.moves} testId="drag-grip" trigger={gripSvg} triggerClass="grip" align="left" />
          </span>
        ) : drag ? (
          <span
            className={"grip" + (drag.locked ? " locked" : "")}
            aria-hidden="true"
            title={drag.locked ? ESTIMATE_PC_MSG.sortLocked : ESTIMATE_PC_MSG.dragHandle}
            data-testid="drag-grip"
            onMouseDown={(e) => {
              e.stopPropagation();
              if (drag.locked) drag.onLocked();
              else drag.onArm();
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {gripSvg}
          </span>
        ) : null}
        <span className="nobox">
          <span className="nonum num">{no}</span>
          {select ? <input type="checkbox" className="axsel" checked={select.on} aria-label={`${r.name || "この行"}を選ぶ`} data-testid="axis-select" onChange={(e) => select.set(e.target.checked)} onClick={(e) => e.stopPropagation()} /> : null}
        </span>
      </div>
    </td>
  );
}
/** 名称の左の「仮」（スマホで作った行。#129・#118） */
function ProvChip({ r }: { r: Row }) {
  return r.role === "detail" && r.lineState === "provisional" ? <span className="chip prov" data-testid="provisional-chip">{AXES_MSG.provisional}</span> : null;
}

/* ---------------------------------------------------------------- 直しているときの印（037）。文字のチップ（色だけにしない） */
export type RowMarkProps = { mark?: RevisionMarkLine | undefined; onRevert?: (() => void) | undefined; busy?: boolean | undefined };
function MarkChip({ change }: { change: keyof typeof MARK_LABEL }) {
  return <span className={"chip mkchip " + (change === "removed" ? "shu" : change === "calc" ? "calc" : "ai")} data-testid={"mark-" + change}>{MARK_LABEL[change]}</span>;
}
function Was({ mark, cell }: { mark: RevisionMarkLine | undefined; cell: MarkCell }) {
  const w = mark?.was.filter((x) => x.cell === cell) ?? [];
  if (!w.length) return null;
  return (
    <>
      {w.map((x, k) => (
        <span className="was" key={k} data-testid={"was-" + cell}>前回 {x.text}</span>
      ))}
    </>
  );
}
function MarkLine({ mark, onRevert, busy }: RowMarkProps) {
  if (!mark) return null;
  return (
    <span className="mkline">
      <MarkChip change={mark.change} />
      {mark.change === "changed" && onRevert ? (
        <button type="button" className="small" disabled={busy} onClick={(e) => { e.stopPropagation(); onRevert(); }} data-testid="revert-line">前回の内容に復元</button>
      ) : null}
    </span>
  );
}
/**
 * 一式の印（#140、10/6 射場さん）：「一式」のチップはそのまま、右に 内訳あり＝箇条書き（3本線に点）・根拠あり＝クリップ のアイコン。内訳なしはアイコンを付けない。
 * 読み上げと乗せたときの文字は今までの「一式：内訳あり」など（aria-label・title）
 */
const LUMP_ICON: Partial<Record<LumpsumBasis, ReactNode>> = {
  breakdown: (
    <svg className="lumpi" viewBox="0 0 24 24" aria-hidden="true" data-icon="list">
      <circle cx="4.5" cy="6" r="1.5" />
      <circle cx="4.5" cy="12" r="1.5" />
      <circle cx="4.5" cy="18" r="1.5" />
      <path d="M9 6h11M9 12h11M9 18h11" />
    </svg>
  ),
  memo: (
    <svg className="lumpi" viewBox="0 0 24 24" aria-hidden="true" data-icon="clip">
      <path d="M20 11.5l-8.2 8.2a5 5 0 0 1-7.1-7.1l8.2-8.2a3.3 3.3 0 0 1 4.7 4.7l-8.2 8.2a1.7 1.7 0 0 1-2.4-2.4l7.6-7.6" />
    </svg>
  ),
};
/** 名称の右の小さな印：一式の印・内訳の行（#130） */
function RowTags({ tk }: { tk: RowTakeoff | undefined }) {
  if (!tk?.basis && tk?.childOf === undefined) return null;
  return (
    <>
      {tk?.basis ? (
        <span className={"lump" + (tk.basis === "none" ? " none" : "")} role="img" data-testid="lumpsum-chip" data-basis={tk.basis} aria-label={TAKEOFF_MSG.lumpsum[tk.basis]} title={TAKEOFF_MSG.lumpsum[tk.basis]}>
          <span className="tag">{ESTIMATE_PC_MSG.lumpsum}</span>
          {LUMP_ICON[tk.basis] ?? null}
        </span>
      ) : null}
      {tk && tk.childOf !== undefined ? <span className="tag" data-testid="child-chip">{tk.childOf ? `${tk.childOf} の内訳` : "内訳の行"}</span> : null}
    </>
  );
}
/** 名称の右に小さく「1F・木工事」（場所・工種が入っている行だけ。社内用。乗せると「場所：1F ＞ LDK」） */
function AxisChips({ place, trade }: { place: AxisName; trade: AxisName }) {
  if (!place && !trade) return null;
  return (
    <span className="axtext" data-testid="axis-cell">
      {place ? <span title={`${AXES_MSG.place}：${place.full}`} data-testid="axis-place">{place.short}</span> : null}
      {place && trade ? "・" : null}
      {trade ? <span title={`${t(AXES_MSG.trade)}：${trade.full}`} data-testid="axis-trade">{trade.short}</span> : null}
    </span>
  );
}
/** 拾い出しの数（#190：乗せたときの「拾い出し n行」） */
const tkTip = (n: number): string => ESTIMATE_PC_MSG.takeoffMark.replace("{n}", String(n));
/** 数量の欄に出す文字：takeoff の行は DB が決めた値（0 も出す）、ほかは 0 を空に（#118：スマホで作った行の数量 0 は空欄で薄く） */
export const qtyText = (r: Row, tk: RowTakeoff | undefined): string => (tk && r.quantitySource === "takeoff" ? (tk.qty ? fq(tk.qty) : "0") : fq(r.quantity ?? 0));

export type LineView = {
  r: Row;
  ctx: TblCtx;
  no: number | "";
  amount: number;
  under: boolean;
  tk?: RowTakeoff | undefined;
  axis: { place: AxisName; trade: AxisName };
  src: SourceInfo;
  /** 小計の行の社内用（その枠の仕入れ） */
  block?: CostSum | undefined;
  drag?: DragProps | undefined;
  select?: { on: boolean; set: (on: boolean) => void } | undefined;
  onEdit: (fid?: string) => void;
  /** 出した回の仕入れ（社内用の列だけ直せる。047） */
  onCostEdit?: ((lineId: string, v: number | null) => void) | undefined;
  /** 率の行の対象の金額（#306。API の計算値＝fn_rate_base_amount。読めていない・テンプレートは undefined、計算できないときは null） */
  rateBaseAmount?: number | null | undefined;
} & RowMarkProps;

/* ---------------------------------------------------------------- 社内用の3つのセル（見る） */
function InternalView({ v }: { v: LineView }) {
  const { r, ctx, amount, under } = v;
  if (!ctx.showInternal) return null;
  // T2：立替金の行は仕入れ・粗利に入れない（仕入れの欄を出さない）
  if (r.role === "detail" && taxCatOf(r) === "reimbursement") {
    return (
      <>
        <td className="int cost r"><span className="muted" title={TAX_MSG.noCostForReimbursement} data-testid="cost-reimbursement">—</span></td>
        <td className="int source" />
      </>
    );
  }
  // #213：パートナー ON の見積の行の手数料（使う内訳の行だけ。見積に付いていなければ undefined）
  const fee = ctx.referral?.byLine[r.id];
  if (r.role === "detail") {
    const issuedCost = ctx.readOnly && v.onCostEdit;
    const own = !!v.src.own;
    return (
      <>
        <td className={"int cost r num" + (under ? " shu" : "") + (issuedCost ? " has-inp" : "")} onClick={(e) => { if (issuedCost) e.stopPropagation(); else { e.stopPropagation(); v.onEdit("f_cost"); } }}>
          <CostTwo
            top={
              issuedCost ? (
                // 出した回でも仕入れ（社内用の列）だけは直せる（047）。外注から入れた行は読むだけ（「…」の「つながりを外す」で手入力に）
                <NumInput kind="int" id={`cost-${r.id}`} showZero value={own ? null : r.costAmount} placeholder={own ? ESTIMATE_PC_MSG.own : ESTIMATE_PC_MSG.costMissing} aria-label={`仕入れ ${r.name}`} data-testid="cost-issued" readOnly={!!v.src.costRef} onChange={(x) => x !== (own ? null : r.costAmount) && v.onCostEdit!(r.id, x)} />
              ) : own ? (
                // 自社（#131 の2回目）：仕入れ 0 として扱う。利益率は出さない（100% と見せない）
                <span className="muted own" data-testid="cost-own">{ESTIMATE_PC_MSG.own}</span>
              ) : r.costAmount != null ? (
                <span className="cost" data-testid="cost">{yen(r.costAmount)}</span>
              ) : (
                <span className="muted" data-testid="cost-missing">{ESTIMATE_PC_MSG.costMissing}</span>
              )
            }
            margin={own ? null : marginOf(amount, r.costAmount == null ? null : r.costAmount + (fee?.fee ?? 0))}
            marginTestId="margin"
          />
        </td>
        <SourceCell r={r} src={v.src} manual={!own && r.costAmount != null && !v.src.costRef} fee={fee} />
      </>
    );
  }
  if (r.role === "subtotal" && v.block) {
    const b = v.block;
    return (
      <>
        <td className="int cost r num">
          <CostTwo top={b.any ? (b.sum || !b.missing ? yen(b.sum) : "—") : null} margin={b.any && !b.missing ? marginOf(amount, b.sum) : null} />
        </td>
        {b.missing ? <MissingCell n={b.missing} /> : <td className="int source" />}
      </>
    );
  }
  if (r.role === "overhead_rate" || r.role === "excluded" || r.role === "discount") {
    return (
      <>
        <td className="int cost r"><span className="muted">—</span></td>
        {r.role === "discount" ? <FeeOnlyCell fee={fee} /> : <SourceCell r={r} src={v.src} manual={false} fee={fee} />}
      </>
    );
  }
  return <>{internalTds(ctx)}</>;
}

/* ---------------------------------------------------------------- 行（見る） */
export function ViewRow(v: LineView) {
  const { r, ctx, no, amount, under, tk, mark, onRevert, busy, drag } = v;
  const cls = ["line", r.role, under ? "under" : "", mark ? "mk-" + mark.change : "", drag?.drop ? "drop-" + drag.drop : "", v.select?.on ? "is-picked" : ""].filter(Boolean).join(" ");
  const ml = <MarkLine mark={mark} onRevert={onRevert} busy={busy} />;
  const was = (cell: MarkCell) => <Was mark={mark} cell={cell} />;
  const click = (e: React.MouseEvent<HTMLTableRowElement>) => {
    const t = e.target as HTMLElement;
    if (t.closest("input,select,button,label,a")) return;
    // 出した見積（行は書けない）：拾い出しのある行の数量を押すと拾い出しの引き出し（出した回でも拾い出しは足せる。手で打った数量でも拾い出しがあれば開く）
    if (ctx.readOnly) {
      if (ctx.takeoff && tk && tk.count > 0 && t.closest("td.qty")) tk.onOpen();
      return;
    }
    v.onEdit(t.closest("td.note") ? "f_note" : t.closest("td.qty") ? "f_qty" : t.closest("td.price") ? "f_price" : t.closest("td.tax") ? "f_tax" : "f_name");
  };
  const dragProps = drag?.enabled ? { draggable: drag.armed, onDragStart: drag.onStart, onDragOver: drag.onOver, onDrop: drag.onDrop, onDragEnd: drag.onEnd } : {};
  const nameText = (ph: string) => (r.name ? <span className="nmt" title={r.name}>{r.name}</span> : <span className="nmt muted">{ph}</span>);
  const axisChips = ctx.showInternal ? <AxisChips place={v.axis.place} trade={v.axis.trade} /> : null;
  const no_ = <NoCell r={r} no={no} drag={drag} select={v.select} />;
  if (r.role === "group" || r.role === "note") {
    /*
     * #302（C2）：見出し行は No〜{lineNote}を1つの枠（No の欄と名称〜{lineNote}の欄の境の線を引かない・文字は名称の列から）。
     * 説明行（文章）は No〜名称を結合し、{lineSpec}から右は枠を残す（明細の行と同じ欄＝見出しと見分ける）。紙（プリセット）も同じ（packages/paper の table.spanRows）
     */
    const isNote = r.role === "note";
    const rest = paperCount(ctx) - 2;
    return (
      <tr className={cls} onClick={click} data-testid="line" {...dragProps}>
        {no_}
        <td className={"name stk merged" + (isNote ? " notetext" : "")} colSpan={isNote ? 1 : paperCount(ctx) - 1} style={{ left: NAME_LEFT }}>
          <div className="nm">
            {r.name ? <span className="nmt" title={r.name}>{r.name}</span> : <span className="nmt muted">{r.role === "group" ? "（見出し）" : "（文章）"}</span>}
            {/* #307：説明の行の「外注待ち」と注記の札はやめた（lineState・noteKind の値は残す） */}
            {axisChips}
            {ml}
            {was("name")}
          </div>
        </td>
        {isNote ? Array.from({ length: Math.max(0, rest) }, (_, k) => <td key={k} className="ncell" />) : null}
        {ctx.showInternal ? (
          <>
            <td className="int cost" />
            {v.src.att || v.src.menu.length || v.src.requested || v.src.ordered?.length ? <SourceCell r={r} src={v.src} manual={false} /> : <td className="int source" />}
          </>
        ) : null}
      </tr>
    );
  }
  if (r.role === "subtotal") {
    return (
      <tr className={cls} onClick={click} data-testid="line" {...dragProps}>
        {no_}
        {/* 小計：名称〜単位に名前、単価〜金額を結合して額（#190：金額は省略しない＝合計の行と同じ形） */}
        <td colSpan={4} className="r lbl">{ml} {r.name || "小計"}{was("name")}</td>
        <td colSpan={2} className="r num amt" data-amt={r.id}>{yen(amount)}{was("amount")}</td>
        {taxEmptyTd(ctx)}
        <td className="note"><span className="cut">{r.note}</span>{was("note")}</td>
        <InternalView v={v} />
      </tr>
    );
  }
  const spec = (
    <td className="spec">
      <span className="cut" title={r.spec || undefined}>{r.spec}</span>
      {r.provisionalNote ? <span className="provnote" data-testid="provisional-note" title={r.provisionalNote}>{r.provisionalNote}</span> : null}
      {was("spec")}
    </td>
  );
  const note = <td className="note"><span className="cut" title={r.note || undefined}>{r.note}</span>{was("note")}</td>;
  if (r.role === "overhead_rate") {
    // #306・#309：費目 → 率 → 対象 の順（「法定福利費・15.37%・対象 労務費」）。費目は field の会社だけ
    const sub = [r.costKind && ctx.field ? COST_KIND_SHORT[r.costKind] || COST_KIND_LABEL[r.costKind] : "", `${pct(r.rate) || "0"}%`, `${ESTIMATE_PC_MSG.rateTarget} ${rateTargetLabel(r.rateBase, ctx.onCover)}`].filter(Boolean).join("・");
    return (
      <tr className={cls} onClick={click} data-testid="line" {...dragProps}>
        {no_}
        <td className="name stk" style={{ left: NAME_LEFT }}>
          <div className="nm">
            {nameText(t("（{lineName}なし）"))}
            <span className="ratesub muted" title={sub}>{sub}</span>
            {ctx.rateBaseUnused?.has(r.id) ? (
              <span className="chip shu" data-testid="rate-base-unused">
                ⚠ {ESTIMATE_PC_MSG.rateBaseUnused}
              </span>
            ) : null}
            {axisChips}
            {ml}
            {was("name")}
          </div>
        </td>
        {spec}
        <td className="r num qty">1{was("quantity")}</td>
        <td className="unit">式{was("unit")}</td>
        <td className="r price">{was("unitPrice")}</td>
        <td className="r num amt" data-amt={r.id}><b>{yen(amount)}</b>{was("amount")}</td>
        {taxViewTd(ctx, r)}
        {note}
        <InternalView v={v} />
      </tr>
    );
  }
  if (r.role === "excluded" || r.role === "discount") {
    return (
      <tr className={cls} onClick={click} data-testid="line" {...dragProps}>
        {no_}
        <td className="name stk" style={{ left: NAME_LEFT }}>
          <div className="nm">
            {nameText(t("（{lineName}なし）"))}
            {r.role === "discount" && r.discountTargetTotal != null ? <span className="ratesub muted">{ESTIMATE_PC_MSG.discountTargetView.replace("{yen}", yen(r.discountTargetTotal))}</span> : null}
            {axisChips}
            {ml}
            {was("name")}
          </div>
        </td>
        {spec}
        <td className="r qty">{was("quantity")}</td>
        <td className="unit">{was("unit")}</td>
        <td className="r price">{was("unitPrice")}</td>
        {r.role === "excluded" ? <td className="r amt"><b>{r.amountLabel || "別途"}</b>{was("amount")}</td> : <td className="r num amt" data-amt={r.id}><b>{yen(amount)}</b>{was("amount")}</td>}
        {taxViewTd(ctx, r)}
        {note}
        <InternalView v={v} />
      </tr>
    );
  }
  const q = qtyText(r, tk);
  return (
    <tr className={cls} onClick={click} data-testid="line" {...dragProps}>
      {no_}
      <td className="name stk" style={{ left: NAME_LEFT }}>
        <div className="nm">
          {tk && tk.childOf !== undefined ? <span className="tkchild" aria-hidden="true">└</span> : null}
          <ProvChip r={r} />
          {nameText(t("（{lineName}なし）"))}
          {ctx.field && isLaborRow(r, ctx) ? <span className="tag" data-testid="labor-tag">{ROW_KIND_LABOR_LABEL}</span> : null}
          <RowTags tk={tk} />
          {axisChips}
          {ml}
          {was("name")}
        </div>
      </td>
      {spec}
      <td className="r num qty">
        {/* #190（10/16 射場さん）：数字の左の巻尺の印をやめ、拾い出しの合計の数量は数字の下に点線（文字より薄い色・2px）。
            点線の数字を押すと拾い出しを開く・乗せると「拾い出し n行」。手で打った数量には出さない（拾い出しがあっても＝行の詳細の「拾い出し n行 ›」から）。
            行の高さは変えない（下線は字の飾り＝箱の高さに入らない） */}
        {ctx.takeoff && tk && tk.count > 0 && r.quantitySource === "takeoff" && !laborKindRow(r, ctx) ? (
          <button type="button" className="tkq" data-testid="takeoff-open" title={tkTip(tk.count)} aria-label={`${r.name || "この行"}の${tkTip(tk.count)}（数量 ${q}）`} onClick={(e) => { e.stopPropagation(); tk.onOpen(); }}>
            <span data-testid="qty" className="tkqv">{q}</span>
          </button>
        ) : (
          <span data-testid="qty" className={q ? undefined : "qty0"}>{q}</span>
        )}
        {was("quantity")}
      </td>
      <td className="unit"><span className="cut">{r.unit}</span>{was("unit")}</td>
      <td className="r num price">{r.unitPrice ? yen(r.unitPrice) : ""}{was("unitPrice")}</td>
      <td className="r num amt" data-amt={r.id}><b>{amount ? yen(amount) : ""}</b>{was("amount")}</td>
      {taxViewTd(ctx, r)}
      {note}
      <InternalView v={v} />
    </tr>
  );
}

/* ---------------------------------------------------------------- 行（書く）：その行の中に入力欄。下に「行の詳細」が1段開く */
export type LineEdit = LineView & {
  /** いま見えている行のうちの位置と、その行たち（率の行の基礎） */
  pos: number;
  rows: Row[];
  onKey: (e: React.KeyboardEvent<HTMLTableRowElement>) => void;
  upd: (p: Partial<Row>) => void;
  /** 区分を変える（#305：「労務費」は画面だけの値＝明細＋労務費の印） */
  onType: (t: RowRole | typeof ROW_KIND_LABOR) => void;
  onInsert: () => void;
  onDelete: () => void;
  onClose: () => void;
  onAmtBlur: (v: string) => boolean;
  focusNext: (id: string, keep?: boolean) => void;
  onNameTyped: (v: string) => void;
  onSearch: () => void;
  pickOpen: boolean;
  axisMode: boolean;
  /** 行の詳細の1行に入れるもの：場所・工種・材工・外注（#129。EstimateAxes の AxisStrip）。#305 で行の詳細の □労務費 はやめた（区分の「労務費」） */
  strip: () => ReactNode;
  /** 仮の行だけ：仮のメモと「確定にする」（2行目） */
  provStrip?: ReactNode;
  /** 単位のプルダウンの先頭に出す単位（品目の既定の単位） */
  unitPreferred?: string | null | undefined;
  onUnlink?: (() => void) | undefined;
  /** 仕入れの欄を出た（#213 ★B：単価が空の行は 既定の粗利率＋パートナー で単価を入れる） */
  onCostBlur?: (() => void) | undefined;
};
/** 区分の「？」の中（説明の要る区分だけ＋金額の欄の近道＝#215）。field の無い会社は 労務費 の区分も、率の対象の「労務費」・費目も書かない（#305・#309） */
const roleHelpOf = (field: boolean) => (
  <>
    {Object.entries(ROW_ROLE_HELP).flatMap(([k, x]) => [
      <span key={k} style={{ display: "block" }}>{k === "overhead_rate" && !field ? ROW_ROLE_HELP_RATE_NO_LABOR : x}</span>,
      ...(k === "group" && field ? [<span key="labor" style={{ display: "block" }}>{ROW_ROLE_HELP_LABOR}</span>] : []),
    ])}
    <span style={{ display: "block" }}>{ROW_ROLE_HELP_AMT}</span>
  </>
);

/**
 * 率の行の対象（#306、docs/103 §2.2）：選ぶ欄は隠し、文字のボタン「労務費 30,000円 ▾」。押すとポップ（枠の合計／小計（選ぶ）／労務費（field だけ）／行（選ぶ））。
 * 金額は API の計算値（rateBaseAmount）。0 円なら朱の文字「対象の行がありません」（#314 から行の詳細の下段の左＝上段を1行に収める）。Enter は行の順路（次の欄へ）、Space・押すとポップ、Esc で閉じる
 */
function RateTarget({ r, ctx, amount, subs, details, upd, focusNext }: {
  r: Row;
  ctx: TblCtx;
  amount: number | null | undefined;
  subs: Row[];
  details: Row[];
  upd: (p: Partial<Row>) => void;
  focusNext: (id: string, keep?: boolean) => void;
}) {
  const [open, setOpen] = useState(false);
  /** ポップの位置（画面に固定＝表の横のスクロールの枠で切れない。ボタンの下、下に入らなければ上） */
  const [pos, setPos] = useState<{ left: number; top?: number; bottom?: number } | null>(null);
  const wrap = useRef<HTMLSpanElement>(null);
  const btn = useRef<HTMLButtonElement>(null);
  const pop = useRef<HTMLSpanElement>(null);
  const focusRef = useRef(focusNext);
  focusRef.current = focusNext;
  useLayoutEffect(() => {
    if (!open || !btn.current) {
      setPos(null);
      return;
    }
    const b = btn.current.getBoundingClientRect();
    const left = Math.max(8, Math.min(b.left, window.innerWidth - 268));
    setPos(window.innerHeight - b.bottom < 260 && b.top > 260 ? { left, bottom: window.innerHeight - b.top + 4 } : { left, top: b.bottom + 4 });
  }, [open]);
  useEffect(() => {
    if (!open) return;
    const onDown = (ev: PointerEvent) => {
      if (!wrap.current?.contains(ev.target as Node)) setOpen(false);
    };
    // 表・画面がスクロールしたら閉じる（ボタンから離れて浮かせない）。ポップの中のスクロールは閉じない
    const onScroll = (ev: Event) => {
      if (!pop.current?.contains(ev.target as Node)) setOpen(false);
    };
    const onResize = () => setOpen(false);
    // Esc はポップだけ閉じる（行の詳細は開いたまま）。WebKit は押したボタンにフォーカスを付けない＝ポップの外で押されても受ける（document の捕捉で先に止める）
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key !== "Escape") return;
      ev.preventDefault();
      ev.stopPropagation();
      setOpen(false);
      focusRef.current("f_base", true);
    };
    document.addEventListener("pointerdown", onDown, true);
    document.addEventListener("keydown", onKey, true);
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onResize);
    return () => {
      document.removeEventListener("keydown", onKey, true);
      document.removeEventListener("pointerdown", onDown, true);
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onResize);
    };
  }, [open]);
  const base = r.rateBase ?? "block";
  const label = rateTargetLabel(base, ctx.onCover);
  const money = amount == null ? "" : `${yen(amount)}円`;
  /** 閉じたらボタンにフォーカスを戻す（WebKit は押したボタンにフォーカスを付けない＝行の Esc・Tab が効くように） */
  const close = () => {
    setOpen(false);
    focusNext("f_base", true);
  };
  const pickBase = (b: RateBase) => {
    const ids = b === "subtotal" ? (subs.length ? [subs[subs.length - 1]!.id] : []) : b === "lines" ? r.rateBaseLineIds : [];
    upd({ rateBase: b, rateBaseLineIds: ids });
    if (b === "block" || b === "labor") close();
  };
  // 「労務費」は field の会社だけ（ほかの会社でも、いまその値の行は選ばれた形で出す）
  const bases = RateBase.options.filter((b) => b !== "labor" || ctx.field || r.rateBase === "labor");
  return (
    <span className="rtgt" ref={wrap}>
      <button type="button" ref={btn} id="f_base" className="rtbtn" data-testid="rate-target" aria-haspopup="menu" aria-expanded={open} aria-label={`${ESTIMATE_PC_MSG.rateTarget}：${label}${money ? ` ${money}` : ""}`} onClick={() => (open ? close() : setOpen(true))}>
        <span className="rtlbl">{label}</span>
        {money ? <span className="num rtamt" data-testid="rate-target-amount">{money}</span> : null}
        <span aria-hidden="true" className="rtcaret">▾</span>
      </button>
      {open && pos ? (
        <span ref={pop} className="rtpop" style={pos} role="menu" aria-label={ESTIMATE_PC_MSG.rateTargetMenu} data-testid="rate-target-pop">
          {bases.map((b) => (
            <button key={b} type="button" role="menuitemradio" aria-checked={base === b} className={"rtopt" + (base === b ? " is-on" : "")} data-testid={`rate-target-${b}`} onClick={() => pickBase(b)}>
              {rateTargetLabel(b, ctx.onCover)}
            </button>
          ))}
          {base === "subtotal" ? (
            <span className="rtsub">
              {subs.length ? (
                <select id="f_baseRef" value={r.rateBaseLineIds[0] ?? ""} aria-label={RATE_BASE_LABEL.subtotal} onChange={(x) => upd({ rateBaseLineIds: x.target.value ? [x.target.value] : [] })}>
                  {subs.map((x) => (
                    <option key={x.id} value={x.id}>{x.name || "小計"}</option>
                  ))}
                  <option value="">{ESTIMATE_PC_MSG.noAxis}</option>
                </select>
              ) : (
                <span className="muted">{ESTIMATE_PC_MSG.rateNoSubtotal}</span>
              )}
            </span>
          ) : null}
          {base === "lines" ? (
            <span className="rtsub rtlines">
              {details.length ? (
                details.map((x) => (
                  <label key={x.id} className="chk" style={{ fontSize: 14, gap: 4 }}>
                    <input type="checkbox" style={{ width: 18, height: 18, margin: 0 }} checked={r.rateBaseLineIds.includes(x.id)} onChange={(c) => upd({ rateBaseLineIds: c.target.checked ? [...r.rateBaseLineIds, x.id] : r.rateBaseLineIds.filter((y) => y !== x.id) })} />
                    {x.name || t("（{lineName}なし）")}
                  </label>
                ))
              ) : (
                <span className="muted">{ESTIMATE_PC_MSG.rateNoDetail}</span>
              )}
            </span>
          ) : null}
        </span>
      ) : null}
    </span>
  );
}

export function EditRow(e: LineEdit) {
  const { r, ctx, no, amount, under, tk, mark, onRevert, busy, upd, onKey } = e;
  const cls = `line ${r.role} ed ${under ? "under" : ""}`;
  const nameInput = (ph?: string) => <input className="ce" id="f_name" value={r.name} placeholder={ph} autoComplete="off" onChange={(x) => upd({ name: x.target.value })} aria-label={t("{lineName}")} />;
  /* ふつうの行と率の行：名称の欄の右に虫眼鏡（候補の引き出し）。R2：アイコンだけの狭いボタン、マウスオーバーとフォーカスで「検索」 */
  const nameWithSearch = (ph?: string) => (
    <div className="namecell">
      {tk && tk.childOf !== undefined ? <span className="tkchild" aria-hidden="true">└</span> : null}
      <ProvChip r={r} />
      <input className="ce" id="f_name" value={r.name} placeholder={ph} autoComplete="off" onChange={(x) => { upd({ name: x.target.value }); e.onNameTyped(x.target.value); }} aria-label={t("{lineName}")} />
      <button type="button" className={"pkbtn" + (e.pickOpen ? " on" : "")} onClick={e.onSearch} aria-label="検索" title="検索" aria-pressed={e.pickOpen} data-tip="検索">
        <Icon k="search" />
      </button>
    </div>
  );
  const specInput = <input className="ce" id="f_spec" value={r.spec} placeholder={t("{lineSpec}")} onChange={(x) => upd({ spec: x.target.value })} aria-label={lineSpecLabel()} />;
  const noteInput = <input className="ce" id="f_note" value={r.note} placeholder={t("{lineNote}")} onChange={(x) => upd({ note: x.target.value })} aria-label={t("{lineNote}")} />;
  const no_ = <NoCell r={r} no={no} />;

  /* 行の種類ごとの追加の欄（行の詳細の2段目）。#314：％計算の行は上段の1行に入れる（rateFields＝区分の「？」の右。2段目は無い） */
  let extra: ReactNode = null;
  let rateFields: ReactNode = null;
  if (r.role === "overhead_rate") {
    const subs = e.rows.slice(0, e.pos).filter((x) => x.role === "subtotal");
    // T2：立替金の行は率の基礎にならない（T1 で計算からも外れている）
    const details = e.rows.filter((x) => x.role === "detail" && taxCatOf(x) !== "reimbursement");
    /** #306：費目を選ぶと、名称が空なら短い名前・率が空なら会社の率の名前の率・対象は 法定福利費 → 労務費／ほか → 枠 */
    const pickKind = (k: CostKind | null) => {
      const p: Partial<Row> = { costKind: k };
      if (k) {
        if (!r.name.trim() && COST_KIND_SHORT[k]) p.name = COST_KIND_SHORT[k];
        if (r.rate == null) {
          const pr = ctx.ratePresets?.find((x) => x.costKind === k);
          if (pr) p.rate = pr.rate;
        }
        p.rateBase = costKindRateBase(k);
        p.rateBaseLineIds = [];
      }
      upd(p);
    };
    // #306：費目 → 率 → 対象（費目は field の会社だけ）。#314：区分の「？」の右に並べる（1280px で1行＝費目 150・率 80・対象 184 が目安）
    rateFields = (
      <span className="axrate" data-testid="rate-detail">
        {ctx.field ? (
          <label className="axf rkind">
            <span className="muted">{ESTIMATE_PC_MSG.costKind}</span>
            <select id="f_kind" value={r.costKind ?? ""} aria-label={ESTIMATE_PC_MSG.costKind} onChange={(x) => pickKind((x.target.value || null) as CostKind | null)}>
              <option value="">{ESTIMATE_PC_MSG.noAxis}</option>
              {CostKind.options.map((k) => (
                <option key={k} value={k}>{COST_KIND_LABEL[k]}</option>
              ))}
            </select>
          </label>
        ) : null}
        <label className="axf rrate">
          <span className="muted">{ESTIMATE_PC_MSG.rate}</span>
          <NumInput kind="dec" id="f_rate" value={r.rate == null ? null : Math.round(r.rate * 10000) / 100} onChange={(v) => upd({ rate: v == null ? null : Math.round(v * 100) / 10000 })} aria-label="率（%）" />
          <span>%</span>
        </label>
        <span className="axf">
          <span className="muted">{ESTIMATE_PC_MSG.rateTarget}</span>
          <RateTarget r={r} ctx={ctx} amount={e.rateBaseAmount} subs={subs} details={details} upd={upd} focusNext={e.focusNext} />
        </span>
      </span>
    );
  } else if (r.role === "discount") {
    extra = (
      <div className="row dline">
        <span className="muted">{ESTIMATE_PC_MSG.discountTarget}</span>
        <NumInput kind="int" id="f_target" style={{ width: 140 }} value={r.discountTargetTotal} aria-label={ESTIMATE_PC_MSG.discountTargetView.replace("{yen}", "○")} onChange={(v) => upd({ discountTargetTotal: v })} />
        <span className="muted">{ESTIMATE_PC_MSG.discountTargetTail}</span>
      </div>
    );
  }
  /* #305：見出しの「見出し内を労務費に設定／解除」はやめた（区分の「労務費」・一括変更の帯の「区分」） */
  const costRef = e.src.costRef;
  /* 行の詳細（#139 で2段に。#131 の2回目は1行）：
       上段＝区分・場所・工種・材工・外注・拾い出し（入力だけ。1280px でも切れない。#307：見出しは区分だけ・説明は区分・場所・外注）
       下段＝仕入元（「仕入元 外注 ○○ 外す」。無ければ空）＋ 右端に「＋ 下に追加」「削除」「×」
     並べ替えは No の左のつまみ（ドラッグか、押して「上へ」「下へ」）。率・値引き・見出し・仮の行・ちがいの印は、要る行だけ上段と下段のあいだに */
  const acts = (
    <tr className="acts" onKeyDown={onKey} data-testid="line-detail">
      <td colSpan={ctx.cols.length}>
        <div className="row dline dmain">
          <label className="axf">
            <span className="muted">{ESTIMATE_PC_MSG.rowKind}</span>
            {/* #305：「労務費」は明細の下（field の会社だけ。画面だけの値＝明細＋労務費の印） */}
            <select id="f_type" value={r.role === "detail" && r.isLabor && ctx.field ? ROW_KIND_LABOR : r.role} aria-label={ESTIMATE_PC_MSG.rowKind} onChange={(x) => e.onType(x.target.value as RowRole | typeof ROW_KIND_LABOR)}>
              {RowRole.options.flatMap((t) => [
                <option key={t} value={t}>{ROW_ROLE_LABEL[t]}</option>,
                ...(t === "detail" && ctx.field ? [<option key={ROW_KIND_LABOR} value={ROW_KIND_LABOR}>{ROW_KIND_LABOR_LABEL}</option>] : []),
              ])}
            </select>
          </label>
          <Help label="区分の説明" text={roleHelpOf(ctx.field)} testId="help-role" />
          {rateFields}
          {e.strip()}
          {TAX_ROLES.has(r.role) && !ctx.taxCol ? (
            <label className="axf">
              <span className="muted">{TAX_MSG.category}</span>
              <TaxSelect id="f_tax2" full value={taxCatOf(r)} onChange={(c) => upd({ taxCategory: c })} />
            </label>
          ) : null}
          {TAX_ROLES.has(r.role) && ctx.taxDateHint ? (
            <>
              {/* S3（★10）：日付の欄は DateInput（カレンダーのボタンを含むので label でくるまない） */}
              <span className="axf">
                <span className="muted">{TAX_MSG.taxDate}</span>
                <DateInput inline className="ce taxdate" id="f_taxdate" label={`行の${TAX_MSG.taxDate}`} ariaLabel={`行の${TAX_MSG.taxDate}`} value={r.taxDate ?? ""} onChange={(v) => upd({ taxDate: v || null })} testId="line-tax-date" />
              </span>
              <Help label={TAX_MSG.taxDate} text={TAX_MSG.taxDateHelp} testId="help-line-tax-date" />
            </>
          ) : null}
          {r.role === "detail" && tk && ctx.takeoff && !laborKindRow(r, ctx) ? (
            <span className="axf">
              <span className="muted">{t(TAKEOFF_MSG.takeoff)}</span>
              <button type="button" className="small tkbtn" data-testid="takeoff-detail" onClick={tk.onOpen}>
                {ESTIMATE_PC_MSG.takeoffCount.replace("{n}", String(tk.count))} ›
              </button>
            </span>
          ) : null}
        </div>
        {extra}
        {r.role === "detail" && ctx.laborBreakdown ? (
          <div className="row dline laborbd" data-testid="labor-breakdown">
            <span className="muted">{ORDER_LINE_MSG.breakdown}</span>
            {LABOR_KEYS.map((k) => (
              <label key={k} className="axf">
                <span className="muted">{LABOR_LABEL[k]}</span>
                <NumInput
                  kind="int"
                  id={`f_lb_${k}`}
                  style={{ width: 110 }}
                  value={r.laborBreakdown?.[k] ?? null}
                  aria-label={LABOR_LABEL[k]}
                  data-testid={`labor-${k}`}
                  onChange={(v) => upd({ laborBreakdown: { ...(r.laborBreakdown ?? {}), [k]: v == null || v < 0 ? null : v } })}
                />
              </label>
            ))}
            {/* 内訳の合計が金額と違うときは文字で（色だけにしない） */}
            {laborSum(r.laborBreakdown) != null && laborSum(r.laborBreakdown) !== Math.round((r.quantity ?? 0) * (r.unitPrice ?? 0)) ? (
              <span className="shu" data-testid="labor-mismatch">{ORDER_LINE_MSG.mismatch.replace("{sum}", yen(laborSum(r.laborBreakdown)!))}</span>
            ) : laborSum(r.laborBreakdown) != null ? (
              <span className="muted">＝ {ORDER_LINE_MSG.amount} {yen(laborSum(r.laborBreakdown)!)}</span>
            ) : null}
          </div>
        ) : null}
        {e.provStrip}
        {mark ? (
          <div className="row dline mkedit" data-testid="mark-edit">
            <MarkChip change={mark.change} />
            {mark.was.length ? <span className="muted">前回 {mark.was.map((w) => (w.label ? `${w.label} ${w.text}` : w.text)).join("、")}</span> : null}
            {mark.change === "changed" && onRevert ? (
              <button type="button" className="small" disabled={busy} onClick={onRevert} data-testid="revert-line">前回の内容に復元</button>
            ) : null}
          </div>
        ) : null}
        <div className="row dline dsub" data-testid="line-detail-sub">
          {/* #306：対象が 0 円なら朱の文字（色だけにしない）。#314：上段を1行に収めるため下段の左に */}
          {r.role === "overhead_rate" && e.rateBaseAmount === 0 ? <span className="shu" data-testid="rate-target-empty">{ESTIMATE_PC_MSG.rateTargetEmpty}</span> : null}
          {/* 仕入元（「森徽章工芸 Q-0001 解除」＝#312）→ 単価［粗利率｜上乗せ］（#213・#314）。社内用を表示している書ける見積だけ（Figma 310:250 の並び） */}
          {costRef && ctx.showInternal ? (
            <span className="srcline">
              <span className="muted">{ESTIMATE_PC_MSG.colSource}</span>
              <CostSource costRef={costRef} role={r.role} onUnlink={e.onUnlink} />
            </span>
          ) : null}
          {ctx.pricing && ctx.showInternal ? <LinePricing r={r} docPct={ctx.referral?.pct ?? null} ctx={ctx.pricing} upd={upd} /> : null}
          <span className="sp" />
          <button type="button" className="small ai" data-testid="insert-below" onClick={e.onInsert}>＋ {ESTIMATE_PC_MSG.addBelow}</button>
          <button type="button" className="small shu" data-testid="delete-line" onClick={e.onDelete}>{ESTIMATE_PC_MSG.deleteRow}</button>
          {/* #138：閉じるは × のアイコン（読み上げ・乗せたときは「閉じる」） */}
          <button type="button" className="small xclose" aria-label={ESTIMATE_PC_MSG.closeRow} title={ESTIMATE_PC_MSG.closeRow} data-testid="close-line" onClick={e.onClose}>
            <Icon k="x" />
          </button>
        </div>
      </td>
    </tr>
  );
  const own = !!e.src.own;
  /** T2：立替金の行は仕入れ・粗利に入れない（仕入れの欄は「—」、利益率も出さない） */
  const reimb = r.role === "detail" && taxCatOf(r) === "reimbursement";
  /* 社内用（#139）：仕入れの欄の上段に入力（幅いっぱい）、下段に利益率 */
  /** #213：パートナー ON の見積の行の手数料（手数料後の利益率・仕入元の欄の下段） */
  const fee = ctx.referral?.byLine[r.id];
  const internalEdit = (cost: ReactNode) =>
    ctx.showInternal ? (
      <>
        <td className={"int cost r" + (under ? " shu" : "") + (r.role === "detail" && !reimb ? " has-inp" : "")}>
          <CostTwo top={cost} margin={r.role === "detail" && !own && !reimb ? marginOf(amount, r.costAmount == null ? null : r.costAmount + (fee?.fee ?? 0)) : null} marginTestId="margin" marginId="c_mg" />
        </td>
        {r.role === "detail" || r.role === "excluded" || r.role === "overhead_rate" ? (
          <SourceCell r={r} src={e.src} manual={r.role === "detail" && !own && r.costAmount != null && !costRef} fee={fee} />
        ) : r.role === "discount" ? (
          <FeeOnlyCell fee={fee} />
        ) : (
          <td className="int source" />
        )}
      </>
    ) : null;

  if (r.role === "subtotal") {
    return (
      <>
        <tr className={cls} onKeyDown={onKey} data-testid="line">
          {no_}
          <td colSpan={4} className="r lbl">
            <input className="ce r subnm" id="f_name" value={r.name} placeholder="小計" onChange={(x) => upd({ name: x.target.value })} aria-label="小計の名前" />
          </td>
          <td colSpan={2} className="r num amt" id="c_amt">{yen(amount)}</td>
          {taxEmptyTd(ctx)}
          <td className="note">{noteInput}</td>
          {ctx.showInternal ? <InternalView v={e} /> : null}
        </tr>
        {acts}
      </>
    );
  }
  if (r.role === "overhead_rate") {
    return (
      <>
        <tr className={cls} onKeyDown={onKey} data-testid="line">
          {no_}
          <td className="name stk" style={{ left: NAME_LEFT }}>{nameWithSearch()}</td>
          <td className="spec">{specInput}</td>
          <td className="r num qty">1</td>
          <td className="unit">式</td>
          <td className="r price" />
          <td className="r num amt" id="c_amt"><b>{yen(amount)}</b></td>
          {ctx.taxCol ? <td className="tax"><TaxSelect value={taxCatOf(r)} onChange={(c) => upd({ taxCategory: c })} /></td> : null}
          <td className="note">{noteInput}</td>
          {internalEdit(<span className="muted">—</span>)}
        </tr>
        {acts}
      </>
    );
  }
  if (r.role === "excluded") {
    return (
      <>
        <tr className={cls} onKeyDown={onKey} data-testid="line">
          {no_}
          <td className="name stk" style={{ left: NAME_LEFT }}>{nameInput()}</td>
          <td className="spec">{specInput}</td>
          <td className="r qty" />
          <td className="unit" />
          <td className="r price" />
          <td className="r amt">
            <input className="ce r" id="f_label" value={r.amountLabel ?? "別途"} list="excl_words" onChange={(x) => upd({ amountLabel: x.target.value })} aria-label="金額欄に印字する文字" />
            <datalist id="excl_words">
              {EXCLUDED_WORDS.map((x) => (
                <option key={x} value={x} />
              ))}
            </datalist>
          </td>
          {taxEmptyTd(ctx)}
          <td className="note">{noteInput}</td>
          {internalEdit(<span className="muted">—</span>)}
        </tr>
        {acts}
      </>
    );
  }
  if (r.role === "discount") {
    return (
      <>
        <tr className={cls} onKeyDown={onKey} data-testid="line">
          {no_}
          <td className="name stk" style={{ left: NAME_LEFT }}>{nameInput()}</td>
          <td className="spec">{specInput}</td>
          <td className="r qty" />
          <td className="unit" />
          <td className="r price" />
          <td className="r amt">
            {r.discountTargetTotal != null ? (
              <b className="num" id="c_amt">{yen(amount)}</b>
            ) : (
              <NumInput kind="int" id="f_price" value={r.amount} placeholder="-8,520" aria-label="値引きの金額" onChange={(v) => upd({ amount: v })} />
            )}
          </td>
          {ctx.taxCol ? <td className="tax"><TaxSelect value={taxCatOf(r)} onChange={(c) => upd({ taxCategory: c })} /></td> : null}
          <td className="note">{noteInput}</td>
          {internalEdit(<span className="muted">—</span>)}
        </tr>
        {acts}
      </>
    );
  }
  if (r.role !== "detail") {
    return (
      <>
        <tr className={cls} onKeyDown={onKey} data-testid="line">
          {no_}
          <td className="name stk" colSpan={paperCount(ctx) - 1} style={{ left: NAME_LEFT }}>{nameInput()}</td>
          {ctx.showInternal ? (
            <>
              <td className="int cost" />
              {e.src.att || e.src.menu.length || e.src.requested || e.src.ordered?.length ? <SourceCell r={r} src={e.src} manual={false} /> : <td className="int source" />}
            </>
          ) : null}
        </tr>
        {acts}
      </>
    );
  }
  /* ふつうの行。数量も単価も空のときは金額の欄が入力欄になる：数字なら「1式」、「10%」なら率の行、「別途」なら別途の行 */
  // 拾い出しの合計の行は、数量の欄が読むだけ（押すと拾い出しの引き出し）。金額の近道（1式にする）も出さない。
  // 拾い出しが 0 本なら手で打てる（#131 の2回目。打つと手で打つに戻す＝DB も 0 本の行は手で打つに戻す）
  // #310：区分「労務費」の行は拾い出しを出さない（数量はそのまま＝手で打つと手入力に戻る）
  const byTakeoff = ctx.takeoff && !!tk && r.quantitySource === "takeoff" && tk.count > 0 && !laborKindRow(r, ctx);
  const blank = !byTakeoff && !r.quantity && !r.unitPrice;
  return (
    <>
      <tr className={cls} onKeyDown={onKey} data-testid="line">
        {no_}
        <td className="name stk" style={{ left: NAME_LEFT }}>{nameWithSearch(t("{lineName}"))}</td>
        <td className="spec">{specInput}</td>
        <td className="r qty">
          <span className="qtycell">
            {byTakeoff ? (
              <button type="button" className="ce r tkqty" id="f_qty" title={tkTip(tk.count)} aria-label={`数量（${tkTip(tk.count)}の合計）`} data-testid="qty-takeoff" onClick={tk.onOpen}><span className="tkqv">{qtyText(r, tk)}</span></button>
            ) : (
              <NumInput kind="dec" id="f_qty" value={r.quantity} placeholder="0" aria-label="数量" onChange={(v) => upd(r.quantitySource === "takeoff" ? { quantity: v, quantitySource: "manual" } : { quantity: v })} />
            )}
          </span>
        </td>
        <td className="unit">
          <UnitSelect value={r.unit} preferred={e.unitPreferred} onChange={(v) => upd({ unit: v })} />
        </td>
        <td className="r price"><NumInput kind="int" id="f_price" value={r.unitPrice} placeholder="0" aria-label="単価" onChange={(v) => upd({ unitPrice: v })} /></td>
        <td className="r num amt" id="c_amt">
          {blank ? (
            // #215：placeholder「金額 / 10% / 別途」をやめた（打ち方は区分の「？」＝ROW_ROLE_HELP_AMT）。近道はそのまま
            <input className="ce r" id="f_amt" defaultValue="" placeholder=" " aria-label="金額（近道）" onBlur={(x) => e.onAmtBlur(x.target.value)} />
          ) : (
            <b>{amount ? yen(amount) : ""}</b>
          )}
        </td>
        {ctx.taxCol ? <td className="tax"><TaxSelect value={taxCatOf(r)} onChange={(c) => upd({ taxCategory: c })} /></td> : null}
        <td className="note">{noteInput}</td>
        {internalEdit(reimb ? <span className="muted" title={TAX_MSG.noCostForReimbursement} data-testid="cost-reimbursement">—</span> : 
          // 自社の行は空の欄に「自社」。数字を打つと手入力（自社は外れる）。空にすると未定（ルール7：分からないものを 0 で埋めない）
          // #311：0 は手入力の 0（未入力に数えない）＝離れても「0」と出す（showZero）
          <NumInput kind="int" id="f_cost" showZero value={own ? null : r.costAmount} placeholder={own ? ESTIMATE_PC_MSG.own : ESTIMATE_PC_MSG.costMissing} aria-label="仕入れ" readOnly={!!costRef} onChange={(v) => {
            if (v != null) upd({ costAmount: v, costOwn: false });
            else if (!own) upd({ costAmount: null });
          }} onBlur={() => e.onCostBlur?.()} />,
        )}
      </tr>
      {acts}
    </>
  );
}

/* ---------------------------------------------------------------- 表紙に並ぶ「内訳の合計」の行（試作 v3.7）
   金額は内訳の紙から自動。名前と備考はこの行の中で書ける。↑↓で内訳の並び＝タブの並びを変える */
export function SecRow({ ctx, sh, no, total, cost, editing, first, last, readOnly, onEdit, onChange, onMove, onOpen, onClose, onNext, mkCount = 0 }: {
  ctx: TblCtx; sh: Sheet; no: number; total: number; cost: CostSum; editing: boolean; first: boolean; last: boolean; readOnly: boolean; mkCount?: number;
  onEdit: (fid: "f_name" | "f_note") => void; onChange: (p: Partial<Sheet>) => void; onMove: (d: -1 | 1) => void; onOpen: () => void; onClose: () => void; onNext: (fid: "f_name" | "f_note") => void;
}) {
  const fixed = (
    <>
      <td className="spec">（別紙 内訳）</td>
      <td className="r num qty">1</td>
      <td className="unit">式</td>
      <td className="r price" />
      <td className="r num amt"><b>{yen(total)}</b></td>
      {taxEmptyTd(ctx)}
    </>
  );
  const internal = ctx.showInternal ? (
    <>
      <td className="int cost r num">
        <CostTwo top={cost.any ? (cost.sum || !cost.missing ? yen(cost.sum) : "—") : null} margin={cost.any && !cost.missing ? marginOf(total, cost.sum) : null} />
      </td>
      {cost.missing ? <MissingCell n={cost.missing} /> : <td className="int source" />}
    </>
  ) : null;
  if (!editing) {
    return (
      <tr className="line cover" data-testid="line" onClick={(e) => { const t = e.target as HTMLElement; if (t.closest("input,select,button,label") || readOnly) return; onEdit(t.closest("td.note") ? "f_note" : "f_name"); }}>
        <td className="no stk"><div className="nocell"><span className="nobox"><span className="nonum num">{no}</span></span></div></td>
        <td className="name stk" style={{ left: NAME_LEFT }}><div className="nm"><span className="nmt">{sh.name || "（名前なし）"}</span>{mkCount > 0 ? <span className="chip ai">変更 {mkCount}</span> : null}</div></td>
        {fixed}
        <td className="note"><span className="cut">{sh.note}</span></td>
        {internal}
      </tr>
    );
  }
  const key = (fid: "f_name" | "f_note") => (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Escape") { e.preventDefault(); onClose(); return; }
    if (e.nativeEvent.isComposing || e.keyCode === 229 || e.key !== "Enter") return;
    e.preventDefault();
    onNext(fid);
  };
  return (
    <>
      <tr className="line cover ed" data-testid="line">
        <td className="no stk"><div className="nocell"><span className="nobox"><span className="nonum num">{no}</span></span></div></td>
        <td className="name stk" style={{ left: NAME_LEFT }}><input className="ce" id="f_name" value={sh.name} placeholder="内訳の名前" onChange={(e) => onChange({ name: e.target.value })} onKeyDown={key("f_name")} aria-label="内訳の名前" /></td>
        {fixed}
        <td className="note"><input className="ce" id="f_note" value={sh.note} placeholder={t("{lineNote}")} onChange={(e) => onChange({ note: e.target.value })} onKeyDown={key("f_note")} aria-label={t("{lineNote}")} /></td>
        {internal}
      </tr>
      <tr className="acts">
        <td colSpan={ctx.cols.length}>
          <div className="row dline">
            <span className="muted">内訳の合計の行</span>
            <button type="button" className="small" onClick={() => onMove(-1)} disabled={first}>↑ 上へ</button>
            <button type="button" className="small" onClick={() => onMove(1)} disabled={last}>↓ 下へ</button>
            <button type="button" className="small ai" onClick={onOpen}>内訳表示 →</button>
            <span className="sp" />
            <button type="button" className="small xclose" aria-label={ESTIMATE_PC_MSG.closeRow} title={ESTIMATE_PC_MSG.closeRow} data-testid="close-sec" onClick={onClose}>
              <Icon k="x" />
            </button>
          </div>
        </td>
      </tr>
    </>
  );
}

/** 消した行：元の場所に薄く残す。押しても書けない。「戻す」で系譜ごと戻る（037） */
export function RemovedRow({ ctx, x, busy, onRestore }: { ctx: TblCtx; x: RevisionMarkRemoved; busy: boolean; onRestore: () => void }) {
  const back = (
    <span className="mkline">
      <MarkChip change="removed" />
      <button type="button" className="small" disabled={busy} onClick={onRestore} data-testid="restore-line">復元</button>
    </span>
  );
  if (x.role === "group" || x.role === "note") {
    return (
      <tr className={`line removed ${x.role}`} data-testid="removed-line">
        <td className="no stk" />
        <td className="name stk" colSpan={paperCount(ctx) - 1} style={{ left: NAME_LEFT }}><div className="nm"><s className="nmt">{x.name || (x.role === "group" ? "（見出し）" : "（文章）")}</s>{back}</div></td>
        {internalTds(ctx)}
      </tr>
    );
  }
  return (
    <tr className={`line removed ${x.role}`} data-testid="removed-line">
      <td className="no stk" />
      <td className="name stk" style={{ left: NAME_LEFT }}><div className="nm"><s className="nmt">{x.name || t("（{lineName}なし）")}</s>{back}</div></td>
      <td className="spec"><s>{x.spec}</s></td>
      <td className="r num qty"><s>{x.quantityText}</s></td>
      <td className="unit"><s>{x.unit}</s></td>
      <td className="r num price"><s>{x.unitPriceText}</s></td>
      <td className="r num amt"><s>{x.amountText}</s></td>
      {taxEmptyTd(ctx)}
      <td className="note"><s>{x.note}</s></td>
      {internalTds(ctx)}
    </tr>
  );
}
/** 消した内訳：表紙に薄く残す。「戻す」で紙ごと・行ごと戻る */
export function RemovedSheetRow({ ctx, x, busy, onRestore }: { ctx: TblCtx; x: RevisionMarkRemovedSheet; busy: boolean; onRestore: () => void }) {
  return (
    <tr className="line cover removed" data-testid="removed-sheet">
      <td className="no stk" />
      <td className="name stk" style={{ left: NAME_LEFT }}>
        <div className="nm">
          <s className="nmt">{x.name || "（名前なし）"}</s>
          <span className="mkline">
            <span className="chip mkchip shu" data-testid="mark-removed">{MARK_LABEL.removed}</span>
            <span className="muted">{x.lineCount}行</span>
            <button type="button" className="small" disabled={busy} onClick={onRestore} data-testid="restore-sheet">復元</button>
          </span>
        </div>
      </td>
      <td className="spec">（別紙 内訳）</td>
      <td className="r num qty"><s>1</s></td>
      <td className="unit"><s>式</s></td>
      <td className="r price" />
      <td className="r num amt"><s>{yen(x.subtotal)}</s></td>
      {taxEmptyTd(ctx)}
      <td className="note" />
      {internalTds(ctx)}
    </tr>
  );
}

/** 並べ方（#129）で作った見出し・「○○ 計」（画面で作る。保存しない） */
export function AxisHeadingRow({ ctx, name, level }: { ctx: TblCtx; name: string; level: number }) {
  return (
    <tr className={"axh lv" + level} data-testid="axis-heading">
      <td className="no stk" />
      <td className="name stk" colSpan={paperCount(ctx) - 1} style={{ left: NAME_LEFT }}>{name}</td>
      {internalTds(ctx)}
    </tr>
  );
}
export function AxisSubtotalRow({ ctx, name, amount, level }: { ctx: TblCtx; name: string; amount: number; level: number }) {
  return (
    <tr className={"axs lv" + level} data-testid="axis-subtotal">
      <td className="no stk" />
      <td colSpan={4} className="r lbl">{name}　計</td>
      <td colSpan={2} className="r num amt">{yen(amount)}</td>
      {taxEmptyTd(ctx)}
      <td className="note" />
      {internalTds(ctx)}
    </tr>
  );
}
export function EmptyRow({ ctx, text, testId }: { ctx: TblCtx; text: string; testId?: string }) {
  return (
    <tr className="emptyrows" data-testid={testId}>
      <td colSpan={ctx.cols.length}>{text}</td>
    </tr>
  );
}

/** 「社内用」のスイッチ：この端末に覚える（アカウントには保存しない）。既定はオン */
const INTERNAL_KEY = "gb.est.internal";
export function useShowInternal(): [boolean, (on: boolean) => void] {
  const [on, setOn] = useState<boolean>(() => {
    try {
      return localStorage.getItem(INTERNAL_KEY) !== "0";
    } catch {
      return true;
    }
  });
  useEffect(() => {
    const f = (e: StorageEvent) => e.key === INTERNAL_KEY && setOn(e.newValue !== "0");
    window.addEventListener("storage", f);
    return () => window.removeEventListener("storage", f);
  }, []);
  return [
    on,
    (v: boolean) => {
      setOn(v);
      try {
        localStorage.setItem(INTERNAL_KEY, v ? "1" : "0");
      } catch {
        /* 控えられない端末では、この画面の間だけ */
      }
    },
  ];
}

